import { Button, Spinner } from '@ibsys/kit';
import React, { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/shared/lib';

type ButtonProps = React.ComponentProps<typeof Button>;

type Props = {
  /**
   * Есть ли в карточке несохранённые изменения.
   *
   * Тот же признак, которым карточка защищается от их потери, — иначе кнопка
   * и подсказка разойдутся с диалогом о потере данных.
   */
  isDirty: boolean;
  onClick: () => void;
  isLoading?: boolean;
  size?: ButtonProps['size'];
  className?: string;
  /**
   * Дополнительное действие карточки (например, «Удалить» или «Сброс»).
   * Размещается между подсказкой об изменениях и кнопкой «Сохранить», чтобы
   * порядок был единым во всех карточках: подсказка → доп. действие → «Сохранить».
   */
  secondaryAction?: ReactNode;
  /**
   * Разрешить сохранение, когда в карточке ничего не менялось, — например, чтобы
   * повторить обработку, которая на сервере не удалась. Подсказка о несохранённых
   * изменениях при этом не показывается: менять в карточке нечего.
   */
  allowSaveWithoutChanges?: boolean;
};

/**
 * Кнопка «Сохранить» с индикацией несохранённых изменений: без изменений неактивна
 * и полупрозрачна, при изменениях активна и сопровождается подсказкой рядом. Пока идёт
 * сохранение, на месте подсказки крутится индикатор загрузки.
 */
export const SaveButton: FC<Props> = ({
  isDirty,
  onClick,
  isLoading,
  size = 'md',
  className,
  secondaryAction,
  allowSaveWithoutChanges = false,
}) => {
  const { t } = useTranslation('common');

  return (
    <div className={cn('flex items-center gap-2', className)}>
      {/* Во время сохранения на месте подсказки крутится индикатор; в самой кнопке
          индикатора нет. Иначе, при наличии изменений, показываем текстовую подсказку. */}
      {isLoading ? (
        <Spinner data-testid="save-loading" className="size-4 text-muted-foreground" />
      ) : (
        isDirty && (
          <span data-testid="unsaved-changes-hint" className="text-sm text-muted-foreground">
            {t('unsavedChanges')}
          </span>
        )
      )}
      {/* Доп. действие (удаление/сброс) — после подсказки, перед «Сохранить». */}
      {/* The action and "Save" are one flex item: a wrapping footer (flex-wrap in className)
          moves only the hint to its own line and never splits the buttons apart */}
      <div className="flex items-center gap-2">
        {secondaryAction}
        <Button
          data-testid="save-button"
          size={size}
          // Единый контурный стиль в обоих состояниях, чтобы размер кнопки не прыгал при
          // переключении: активная — полная непрозрачность, неактивная — полупрозрачная
          // (за счёт встроенного disabled:opacity кнопки).
          variant="outline"
          disabled={(!isDirty && !allowSaveWithoutChanges) || isLoading}
          onClick={onClick}
        >
          {t('save')}
        </Button>
      </div>
    </div>
  );
};
